/** biome-ignore-all lint/correctness/useExhaustiveDependencies: TODO: fix */
/** biome-ignore-all lint/correctness/useUniqueElementIds: TODO: fix */

import { useEffect, useState } from 'react';
import { Col, Form, Modal, Radio, Row } from 'antd';
import { t } from 'i18next';
import moment from 'moment';

import { ENV_VARS } from '@/env';
import { timeZonesList } from '@/contants/timeZones';

import { ReactComponent as CalendarIcon } from '../../../../../../../assets/icons/calendar-check-02.svg';
import { ReactComponent as CloseIcon } from '../../../../../../../assets/icons/x-close.svg';
import Button from '../../../../../../components/Buttons/Button';
import IconButton from '../../../../../../components/Buttons/IconButton';
import SvgConverter from '../../../../../../components/Common/SvgConverter';
import FormDatePicker from '../../../../../../components/Inputs/FormDatePicker';
import FormTimePicker from '../../../../../../components/Inputs/FormTimePicker';
import FormLabel from '../../../../../../components/Labels/FormLabel';
import SpotBidExpireTimeSelector from '../../../../../../components/Selectors/SpotBidExpireTimeSelector';
import TimeZoneSelectorHB from '../../../../../../components/Selectors/TimeZoneSelectorHB';

const isHBUser = ENV_VARS.REACT_APP_THEME === 'halliburton';

const BidDurationModal = ({
  open = false,
  okText = t('translation:default.options.ok'),
  onClose = () => {},
  onOk = () => {},
  okButtonVariant = 'primary',
  alertTitle = t('translation:default.fields.alert'),
  setUserTZInitially = false,
  bidExpirationValue,
}) => {
  const [radio, setRadio] = useState('dateHours');
  const [form] = Form.useForm();
  const isDateHours = radio === 'dateHours';
  const isDateTime = radio === 'dateTime';

  const handleChange = (event) => {
    setRadio(event.target.id);
    if (event.target.id === 'dateHours') {
      form.setFieldValue('SpotBidTimeZoneValue', null);
      form.setFieldValue('selectTime', null);
      form.setFieldValue('ianaTimeZoneId', null);
    } else {
      form.setFieldValue('selectHours', null);
    }
  };

  const handleTimeZoneChange = (value) => {
    form.setFieldValue('ianaTimeZoneId', value.ianaTimeZoneId);
  };

  const handleClickOk = () => {
    onOk(form.getFieldsValue(true));
  };

  useEffect(() => {
    if (bidExpirationValue) {
      form.setFieldValue('selectDate', bidExpirationValue);
    }
  }, []);

  useEffect(() => {
    if (isDateTime && setUserTZInitially) {
      const tz = moment.tz.guess();
      const differenceWithUtc = moment().tz(tz).format('Z');
      const tzInfo = tz.split('/');
      const tzCountry = tzInfo?.length > 1 && tzInfo[1];

      const searchConditions = [
        // search directly by IANA
        (value) => value.ianaTimeZoneId === tz,
        // handle IANA edge cases
        (value) =>
          (value.ianaTimeZoneId === 'Europe/Kyiv' && tz === 'Europe/Kiev') ||
          (value.ianaTimeZoneId === 'Asia/Kolkata' && tz === 'Asia/Calcutta'),
        // pick by UTC difference
        (value) => (differenceWithUtc ? value.displayName.includes(differenceWithUtc) : false),
        // pick by country
        (value) => (tzCountry ? value.displayName.includes(tzCountry) : false),
      ];

      // use search conditions by priority to find timezone
      const selectValue = searchConditions.map((predicate) => timeZonesList.timeZones.find(predicate))?.find(Boolean);

      if (selectValue) {
        form.setFieldValue('SpotBidTimeZoneValue', selectValue.windowsTimeZoneId);
        form.setFieldValue('ianaTimeZoneId', selectValue.ianaTimeZoneId);
      }
    }
  }, [radio]);

  return (
    <Modal
      rootClassName="common-root-bid-description-modal"
      className="common-bid-description-modal"
      closable={false}
      centered
      footer={false}
      open={open}
    >
      <Form form={form} name="expirationDateModalForm">
        <Row className="header-section" justify="space-between" align="middle">
          <Col>
            <div className="wrapper">
              <div className="iconWrapper">
                <SvgConverter Icon={CalendarIcon} color="white" width={24} height={24} />
              </div>
              {alertTitle}
            </div>
          </Col>
          <Col className="lh-0">
            <IconButton onClick={onClose}>
              <SvgConverter Icon={CloseIcon} color="white" width={24} height={24} />
            </IconButton>
          </Col>
        </Row>
        <div className="content-section">
          <FormLabel>Expiration Date</FormLabel>
          <div className="wrapper">
            <div className={`radio-wrapper ${isDateHours && 'active'}`}>
              <Radio checked={isDateHours} id="dateHours" onChange={handleChange}>
                <label className="f-13 fw-600">Date & Hours</label>
              </Radio>
            </div>
            <div className={`radio-wrapper ${isDateTime && 'active'}`}>
              <Radio checked={isDateTime} onChange={handleChange} id="dateTime">
                <label className="f-13 fw-600">Date & Time</label>
              </Radio>
            </div>
          </div>

          <div className="date-wrapper">
            <div className="date">
              <FormDatePicker
                isPrefTzSupport={false}
                name="selectDate"
                label="Date"
                margin="m-0"
                disabledTargetDate={Date.now()}
              />
            </div>
            <div className="date">
              {isDateHours ? (
                <SpotBidExpireTimeSelector
                  checkBoxClass="normal-checkbox"
                  label="Hours"
                  name="selectHours"
                  margin="m-0"
                />
              ) : (
                <FormTimePicker label="Time" editMode name="selectTime" margin="m-0" isPrefTzSupport={false} />
              )}
            </div>
          </div>
          <div className={`time-zone-wrapper ${isDateTime && 'time-zone-wrapper-active'}`}>
            <TimeZoneSelectorHB
              label="Time Zone"
              name="SpotBidTimeZoneValue"
              editMode
              margin="m-0"
              handleChange={handleTimeZoneChange}
            />
          </div>
          <Row className="footer-section" justify="end">
            <Col>
              <Row gutter={12}>
                <Col>
                  <Button
                    id="confirmButton"
                    variant={okButtonVariant}
                    onClick={handleClickOk}
                    className={`submit-btn ${isHBUser ? 'hb-button' : ''}`}
                    dataTestId="bid-duration-modal-submit"
                  >
                    {okText}
                  </Button>
                </Col>
                <Col>
                  <Button
                    id="cancelButton"
                    variant={okButtonVariant}
                    onClick={onClose}
                    type="default"
                    className="cancel-btn"
                  >
                    Cancel
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      </Form>
    </Modal>
  );
};

export default BidDurationModal;
